import { useMemo } from 'react';

const statusLabels = {
    new: 'Nouveaux',
    preparing: 'En préparation',
    ready: 'Prêts',
};

export default function CuisineTab({ tickets, menuItems, onUpdateTicket, onConsumeTicketStock }) {
    const grouped = useMemo(
        () => ({
            new: tickets.filter((ticket) => ticket.status === 'new'),
            preparing: tickets.filter((ticket) => ticket.status === 'preparing'),
            ready: tickets.filter((ticket) => ticket.status === 'ready'),
        }),
        [tickets]
    );

    const getMenuItem = (menuItemId) => menuItems.find((item) => item.id === menuItemId);

    const getElapsedMinutes = (dateString) => {
        const diffMs = new Date().getTime() - new Date(dateString).getTime();
        return Math.max(0, Math.floor(diffMs / 60000));
    };

    const togglePreparation = (ticketId) => {
        onUpdateTicket(ticketId, { status: 'preparing' });
    };

    const toggleItemReady = (ticketId, menuItemId) => {
        const ticket = tickets.find((item) => item.id === ticketId);
        if (!ticket) return;

        const items = ticket.items.map((item) =>
            item.menuItemId === menuItemId ? { ...item, ready: !item.ready } : item
        );

        onUpdateTicket(ticketId, { items });
    };

    const validateReady = (ticketId) => {
        onUpdateTicket(ticketId, { status: 'ready' });
        onConsumeTicketStock(ticketId);
    };

    const renderTicket = (ticket) => {
        const elapsed = getElapsedMinutes(ticket.createdAt);
        const readyCount = ticket.items.filter((item) => item.ready).length;
        const allReady = readyCount === ticket.items.length && ticket.items.length > 0;

        return (
            <article key={ticket.id} className="rounded-3xl border border-green-400 bg-green-50 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.3em] text-green-400">{ticket.tableName}</p>
                        <h3 className="text-xl font-semibold text-green-900">Bon #{ticket.id}</h3>
                    </div>
                    <div className="rounded-3xl bg-white px-4 py-3 text-sm text-green-700">
                        <span className="font-semibold text-green-900">{elapsed} min</span>
                        <span className="ml-2 text-green-600">depuis le lancement</span>
                    </div>
                </div>

                {ticket.notes && (
                    <div className="mt-4 rounded-3xl border border-yellow-300 bg-yellow-50 p-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-yellow-600 font-semibold">Notes / Remarques</p>
                        <p className="mt-2 text-sm text-yellow-900 whitespace-pre-wrap">{ticket.notes}</p>
                    </div>
                )}

                <div className="mt-4 space-y-3">
                    {ticket.items.map((item) => {
                        const menuItem = getMenuItem(item.menuItemId);
                        return (
                            <div
                                key={item.menuItemId}
                                className="rounded-3xl border border-green-400 bg-white p-4"
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <p className="font-semibold text-green-900">{menuItem?.name}</p>
                                        <p className="text-sm text-green-500">Quantité : {item.quantity}</p>
                                    </div>
                                    <button
                                        className={`rounded-full px-3 py-1 text-xs font-semibold uppercase transition ${
                                            item.ready
                                                ? 'bg-green-600 text-slate-950'
                                                : 'border border-green-300 bg-green-50 text-green-800 hover:bg-white'
                                        }`}
                                        onClick={() => toggleItemReady(ticket.id, item.menuItemId)}
                                    >
                                        {item.ready ? 'Prêt' : 'Marquer prêt'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    {ticket.status === 'new' && (
                        <button
                            onClick={() => togglePreparation(ticket.id)}
                            className="rounded-3xl bg-green-600 px-4 py-3 font-semibold text-slate-950 transition hover:bg-green-500"
                        >
                            Démarrer la préparation
                        </button>
                    )}
                    {ticket.status === 'preparing' && (
                        <button
                            disabled={!allReady}
                            onClick={() => validateReady(ticket.id)}
                            className="rounded-3xl bg-green-600 px-4 py-3 font-semibold text-slate-950 transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Valider bon complet
                        </button>
                    )}
                    <span className="rounded-3xl border border-green-300 bg-white px-4 py-3 text-sm text-green-700">
                        {ticket.status === 'new' && 'Nouveau'}
                        {ticket.status === 'preparing' && 'En préparation'}
                        {ticket.status === 'ready' && 'Prêt'}
                    </span>
                </div>
            </article>
        );
    };

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Cuisine</p>
                        <h2 className="text-3xl font-semibold text-green-900">Flux de préparation</h2>
                    </div>
                    <p className="max-w-xl text-sm text-green-600">
                        Gère les bons, les statuts de préparation et le flux de cuisine. Chaque préparation consomme le stock dès que le ticket est finalisé.
                    </p>
                </div>
            </section>

            <div className="grid gap-6 lg:grid-cols-3">
                {['new', 'preparing', 'ready'].map((status) => (
                    <div key={status} className="rounded-3xl border border-green-400 bg-white p-4">
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <p className="text-sm uppercase tracking-[0.3em] text-green-500">{statusLabels[status]}</p>
                                <h3 className="text-xl font-semibold text-green-900">{grouped[status].length}</h3>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {grouped[status].length ? grouped[status].map(renderTicket) : <p className="text-sm text-green-500">Aucun ticket.</p>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
